import Component from '@glimmer/component';
import { modifier } from 'ember-modifier';

// Sentinel is the bare stuck-detection marker: a zero-height element the
// IntersectionObserver watches to tell when the pinned region has reached its
// pinned position. It renders no content and takes no part in the layout, so
// consumers don't have to strip chrome off it.
//
// Use it when the pinned content is the only content — it stays in flow the
// whole time and simply gains its stuck styling once the sentinel scrolls past
// the handoff line:
//
//   <StickyElement as |sticky|>
//     <sticky.sentinel />
//     <sticky.pinned>...always visible, pins in place...</sticky.pinned>
//   </StickyElement>
//
// A morphing banner uses <sticky.scrollable> instead, which is this same
// detection wrapped around real expanded content.
//
// The handoff line sits `handoffOffset` px below the container top (a negative
// top rootMargin). Setting it to the pinned bar's height makes the bar take
// over while the expanded region still fills that band, so the swap happens
// in place with no gap. It defaults to `topOffset` (hand off at the pin line).
export default class StickyElementSentinel extends Component {
  watch = modifier((element) => {
    let selector = this.args.scrollContainerSelector;
    let root = selector ? document.querySelector(selector) : null;
    let handoffOffset = this.args.handoffOffset ?? this.args.topOffset ?? 0;
    let observer = new IntersectionObserver(
      ([entry]) => {
        // Non-intersection happens both when the region has scrolled off the
        // top and when it is still below the fold. Only the former should pin
        // the bar, so confirm the region is actually above the handoff line
        // before reporting it stuck.
        let rootTop = entry.rootBounds?.top ?? 0;
        let scrolledPastTop =
          !entry.isIntersecting && entry.boundingClientRect.top < rootTop;
        this.args.onStuck?.(scrolledPastTop);
      },
      {
        root,
        rootMargin: `-${handoffOffset}px 0px 0px 0px`,
        threshold: 0,
      },
    );
    observer.observe(element);
    return () => observer.disconnect();
  });
}
